import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChart3, TrendingUp, Clock, BookOpen, Target, Award, Star, Volume2 } from 'lucide-react'; // Added Star, Volume2
import apiService from '../services/apiService';

const DashboardContainer = styled.div`
  padding: 20px 12px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    padding: 40px 20px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #1a202c;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (min-width: 768px) {
    font-size: 32px;
    margin-bottom: 30px;
    gap: 12px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
    margin-bottom: 40px;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (min-width: 768px) {
    padding: 24px;
    gap: 16px;
  }
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => props.$color || '#3b82f6'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  
  @media (min-width: 768px) {
    width: 60px;
    height: 60px;
    border-radius: 12px;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatNumber = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #1a202c;
  margin-bottom: 4px;
  
  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 12px;
  
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const StatChange = styled.div`
  font-size: 10px;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  display: flex;
  align-items: center;
  gap: 4px;
  
  @media (min-width: 768px) {
    font-size: 12px;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

const ChartCard = styled.div`
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: 350px;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    padding: 24px;
    height: 400px;
  }
`;

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 16px;
  
  @media (min-width: 768px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const ChartContainer = styled.div`
  flex: 1;
  width: 100%;
`;

const RecentActivity = styled.div`
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  
  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const ActivityTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 16px;
  
  @media (min-width: 768px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background: #f8fafc;
  
  @media (min-width: 768px) {
    gap: 12px;
    padding: 12px;
  }
`;

const ActivityIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #eff6ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  
  @media (min-width: 768px) {
    width: 40px;
    height: 40px;
    border-radius: 8px;
  }
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityText = styled.div`
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
  font-size: 14px;
  
  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const ActivityTime = styled.div`
  font-size: 10px;
  color: #9ca3af;
  
  @media (min-width: 768px) {
    font-size: 12px;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1rem;
`;

function Dashboard() {
  const [stats, setStats] = useState({
    totalWords: 0,
    totalSentences: 0,
    studiedToday: 0,
    totalFavorites: 0,
    studyStreak: 0,
    accuracy: 0
  });

  const [ttsStatus, setTtsStatus] = useState({ status: 'checking', message: '확인 중...' });

  const [chartData, setChartData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user || !user.id) {
        setLoading(false);
        return;
      }

      const res = await apiService.getDashboardStats(user.id);
      if (res.success) {
        setStats(res.data.stats);
        setChartData(res.data.chartData);
        setRecentActivity(res.data.recentActivities);
      }

      // Check TTS Health
      try {
        await apiService.checkTTSHealth();
        setTtsStatus({ status: 'ok', message: '정상 작동 (Online)' });
      } catch (e) {
        setTtsStatus({ status: 'error', message: '연결 실패 (Offline)' });
      }
    } catch (err) {
      console.error('Failed to load dashboard data', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <Title>
          <BarChart3 size={32} />
          학습 대시보드
        </Title>
        <LoadingText>데이터를 불러오는 중입니다...</LoadingText>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Title>
        <BarChart3 size={32} />
        학습 대시보드
      </Title>

      <StatsGrid>
        <StatCard>
          <StatIcon $color="#3b82f6">
            <BookOpen size={24} />
          </StatIcon>
          <StatContent>
            <StatNumber>{stats.totalWords}</StatNumber>
            <StatLabel>저장된 단어</StatLabel>
            <StatChange $positive={true}>
              <TrendingUp size={12} />
              누적 합계
            </StatChange>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $color="#10b981">
            <Target size={24} />
          </StatIcon>
          <StatContent>
            <StatNumber>{stats.studiedToday}</StatNumber>
            <StatLabel>오늘 학습 단어</StatLabel>
            <StatChange $positive={stats.studiedToday > 0}>
              <TrendingUp size={12} />
              {stats.studiedToday > 0 ? '학습 중' : '시작하세요!'}
            </StatChange>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $color="#f59e0b">
            <Star size={24} />
          </StatIcon>
          <StatContent>
            <StatNumber>{stats.totalFavorites}</StatNumber>
            <StatLabel>즐겨찾기 단어</StatLabel>
            <StatChange $positive={true}>
              중요 단어
            </StatChange>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $color="#8b5cf6">
            <Clock size={24} />
          </StatIcon>
          <StatContent>
            <StatNumber>{stats.totalSentences}</StatNumber>
            <StatLabel>저장된 문장</StatLabel>
            <StatChange $positive={true}>
              누적 합계
            </StatChange>
          </StatContent>
        </StatCard>


        {/* TTS Status Card */}
        <StatCard>
          <StatIcon $color={ttsStatus.status === 'ok' ? '#10b981' : (ttsStatus.status === 'checking' ? '#6b7280' : '#ef4444')}>
            <Volume2 size={24} />
          </StatIcon>
          <StatContent>
            <StatNumber style={{ fontSize: '18px' }}>
              {ttsStatus.status === 'ok' ? 'AI 음성 (Online)' : (ttsStatus.status === 'checking' ? '연결 확인 중...' : 'AI 음성 오류')}
            </StatNumber>
            <StatLabel>서버 TTS 상태</StatLabel>
            <StatChange $positive={ttsStatus.status === 'ok'}>
              {ttsStatus.message}
            </StatChange>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>최근 7일 학습 현황</ChartTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="words" fill="#3b82f6" name="학습 단어 수" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartCard>
      </ChartsGrid>

      <RecentActivity>
        <ActivityTitle>최근 활동 (단어 & 문장)</ActivityTitle>
        <ActivityList>
          {recentActivity.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              최근 활동이 없습니다.
            </div>
          ) : (
            recentActivity.map((activity, idx) => (
              <ActivityItem key={`${activity.id}-${idx}`}>
                <ActivityIcon>
                  <BookOpen size={20} />
                </ActivityIcon>
                <ActivityContent>
                  <ActivityText>
                    "{activity.text}" {activity.action}
                  </ActivityText>
                  <ActivityTime>
                    {new Date(activity.time).toLocaleString('ko-KR')}
                  </ActivityTime>
                </ActivityContent>
              </ActivityItem>
            ))
          )}
        </ActivityList>
      </RecentActivity>
    </DashboardContainer >
  );
}

export default Dashboard;
